import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Calendar, Dumbbell, LayoutTemplate, LineChart, Settings, LogOut } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';

export default function AppLayout() {
    const { user, logout } = useAuth();
    const location = useLocation();
    // Hide global chrome while in workout mode (distraction-free)
    const isWorkout = location.pathname.startsWith('/workout/');

    const navItems = [
        { to: '/', label: 'Calendario', icon: Calendar, testid: 'nav-calendar' },
        { to: '/exercises', label: 'Ejercicios', icon: Dumbbell, testid: 'nav-exercises' },
        { to: '/templates', label: 'Plantillas', icon: LayoutTemplate, testid: 'nav-templates' },
        { to: '/progress', label: 'Progreso', icon: LineChart, testid: 'nav-progress' },
        { to: '/settings', label: 'Ajustes', icon: Settings, testid: 'nav-settings' },
    ];

    if (isWorkout) {
        // Give full canvas for workout mode
        return <div className="min-h-screen bg-background app-bg-noise"><Outlet /></div>;
    }

    return (
        <div className="min-h-screen bg-background app-bg-noise text-foreground">
            <div className="lg:flex">
                {/* Sidebar (desktop/iPad) */}
                <aside className="hidden lg:flex lg:flex-col lg:w-[240px] xl:w-[260px] border-r border-border bg-[hsl(var(--surface-1))] p-4 sticky top-0 h-screen">
                    <div className="mb-6 pt-1 pb-3">
                        <div className="flex items-center gap-2">
                            <div className="h-9 w-9 rounded-xl bg-[hsl(var(--primary))] grid place-items-center text-[hsl(var(--primary-foreground))] font-bold">F</div>
                            <div>
                                <div className="font-display font-semibold tracking-tight text-lg">FitHome</div>
                                <div className="text-xs text-muted-foreground">Tu entreno en casa</div>
                            </div>
                        </div>
                    </div>
                    <nav className="flex flex-col gap-1">
                        {navItems.map((it) => (
                            <NavLink
                                key={it.to}
                                to={it.to}
                                end={it.to === '/'}
                                data-testid={it.testid}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 rounded-[12px] px-3 h-11 text-sm transition-colors ${
                                        isActive ? 'bg-[hsl(var(--surface-2))] text-foreground border border-border' : 'text-muted-foreground hover:bg-[hsl(var(--surface-2))] hover:text-foreground'
                                    }`
                                }
                            >
                                <it.icon className="h-4 w-4" />
                                {it.label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="mt-auto pt-4 border-t border-border">
                        <div className="text-xs text-muted-foreground mb-2 truncate" data-testid="sidebar-user-name">{user?.name}</div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={logout}
                            className="w-full justify-start gap-2 text-muted-foreground"
                            data-testid="logout-button"
                        >
                            <LogOut className="h-4 w-4" />
                            Cerrar sesión
                        </Button>
                    </div>
                </aside>

                {/* Mobile top bar */}
                <div className="lg:hidden sticky top-0 z-30 bg-[hsl(var(--surface-1))] border-b border-border px-4 h-14 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-[hsl(var(--primary))] grid place-items-center text-[hsl(var(--primary-foreground))] font-bold text-sm">F</div>
                        <div className="font-display font-semibold">FitHome</div>
                    </div>
                    <div className="text-xs text-muted-foreground truncate max-w-[50%]">{user?.name}</div>
                </div>

                <main className="flex-1 min-w-0">
                    <Outlet />
                </main>
            </div>

            {/* Mobile bottom nav */}
            <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-[hsl(var(--surface-1))] border-t border-border px-2 pb-[env(safe-area-inset-bottom)]">
                <div className="grid grid-cols-5">
                    {navItems.map((it) => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            end={it.to === '/'}
                            data-testid={`${it.testid}-mobile`}
                            className={({ isActive }) =>
                                `flex flex-col items-center justify-center gap-1 py-2 text-[11px] ${
                                    isActive ? 'text-[hsl(var(--primary))]' : 'text-muted-foreground'
                                }`
                            }
                        >
                            <it.icon className="h-5 w-5" />
                            {it.label}
                        </NavLink>
                    ))}
                </div>
            </nav>
            <div className="lg:hidden h-16" />
        </div>
    );
}
